import React, { useEffect, useState } from "react";
import { api } from "../utils/api";
import { PageHeader } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { TableCard, td, trStyle } from "../components/ui/Table";
import { EmptyState, LoadingRow } from "../components/ui/EmptyState";
import { PreviewModal } from "../components/ui/Modal";
import { colors, radius } from "../components/ui/tokens";
import { TrashIcon, EyeIcon } from "../components/ui/icons";

const CirclesIcon = ({ size = 40, color = colors.textFaint }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="4" />
  </svg>
);

const KIND_TONE: Record<string, { bg: string; fg: string; label: string }> = {
  vibe: { bg: "#F0F0FF", fg: "#4A3A8B", label: "Vibe" },
  real: { bg: "#FFF0F0", fg: "#E24A2B", label: "Real" },
  media: { bg: "#F0FFF7", fg: "#1D9E75", label: "Media Vibe" },
  live: { bg: "#FDF3DD", fg: "#B7791F", label: "Live" },
};

// Modération unifiée des "Vibes" (stories, 24h) et "Lives" (vidéos de profil de moins de 24h) —
// deux sources distinctes en base (stories / profile_photos) fusionnées ici comme le fait déjà
// l'app elle-même (voir GET /venues/:id/vibes qui les mélange en un seul flux). Équipe de
// modération (admin ou modérateur) : voir ce qui circule en ce moment, supprimer si besoin.
export default function Vibes() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<any | null>(null);

  const load = () => {
    Promise.all([
      api.get("/stories/admin/all").then(data => Array.isArray(data?.stories) ? data.stories : []).catch(() => []),
      api.get("/users/admin/lives").then(data => Array.isArray(data?.lives) ? data.lives.map((l: any) => ({ ...l, kind: "live" })) : []).catch(() => []),
    ]).then(([stories, lives]) => {
      const merged = [...stories, ...lives].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setItems(merged);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = (item: any) => {
    if (!window.confirm(`Supprimer cette ${KIND_TONE[item.kind]?.label || item.kind} ?`)) return;
    const path = item.kind === "live" ? `/users/admin/lives/${item.id}` : `/stories/${item.id}`;
    api.delete(path).then(() => setItems(prev => prev.filter(i => i.id !== item.id))).catch(() => {});
  };

  return (
    <div style={{ padding: 32 }}>
      <PageHeader title="Vibes & Lives" subtitle={`${items.length} actives`} />

      <TableCard headers={["Auteur", "Type", "Contenu", "Publiée", "Actions"]}>
        {loading ? (
          <tr><td colSpan={5}><LoadingRow /></td></tr>
        ) : items.length === 0 ? (
          <tr><td colSpan={5}><EmptyState title="Aucune vibe ou live en ce moment" icon={<CirclesIcon />} /></td></tr>
        ) : items.map(item => {
          const tone = KIND_TONE[item.kind] || KIND_TONE.vibe;
          return (
            <tr key={`${item.kind}-${item.id}`} style={trStyle}>
              <td style={{ ...td, fontWeight: 600 }}>{item.author_name}</td>
              <td style={td}><span style={{ backgroundColor: tone.bg, color: tone.fg, fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: radius.pill }}>{tone.label}</span></td>
              <td style={td}>
                {item.caption ? <span style={{ color: colors.textMuted, fontSize: 12.5 }}>{item.caption}</span> : <span style={{ color: colors.textFaint, fontSize: 12.5 }}>—</span>}
              </td>
              <td style={{ ...td, color: colors.textFaint, fontSize: 12.5 }}>{new Date(item.created_at).toLocaleString("fr-FR")}</td>
              <td style={td}>
                <div style={{ display: "flex", gap: 6 }}>
                  <Button small variant="secondary" onClick={() => setPreview(item)}><EyeIcon size={13} /> Consulter</Button>
                  <Button small variant="danger" onClick={() => remove(item)}><TrashIcon size={13} /> Supprimer</Button>
                </div>
              </td>
            </tr>
          );
        })}
      </TableCard>

      <PreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        title={preview ? `${KIND_TONE[preview.kind]?.label || preview.kind} de ${preview.author_name}` : ""}
        subtitle={preview?.author_email}
        image={preview && preview.kind !== "live" ? preview.media_url : null}
        body={preview?.caption}
        fields={preview ? [
          { label: "Publiée le", value: new Date(preview.created_at).toLocaleString("fr-FR") },
          { label: "Média", value: (preview.media_url || preview.url) ? <a href={preview.media_url || preview.url} target="_blank" rel="noreferrer" style={{ color: colors.accent }}>Ouvrir ↗</a> : "—" },
        ] : []}
      />
    </div>
  );
}
